import { useFlag } from '@calendly/feature-check-react'

import { MEETINGS_RECAP_SCOPE } from 'features/recaps'
import {
  useGQLGetMeetingQuery,
  useGetMeetingByEitherIdSupergraphQuery,
} from 'shared/api/meetings'
import { transformGetMeetingSupergraphResponse } from 'shared/api/meetings/transformers/get_meeting_supergraph_response'

const isSupergraphResultEmpty = (data) => {
  if (data == null) return true
  if (data.uuid) return false
  return transformGetMeetingSupergraphResponse(data) == null
}

/**
 * Meeting payload from GraphQL — search-api (`/api/search`) or cmd-api via supergraph.
 * When `sa_meetings_page_with_supergraph` is on, tries supergraph first and falls back to
 * search-api when the supergraph result is empty.
 *
 * @param {string | undefined} externalId — foreign event id (search-api uuid / cmd-api external uuid)
 */
export const useSupergraphQuery = (meetingId, options = {}) => {
  const { skip = false, ...queryOptions } = options
  const shouldUseSupergraph = useFlag('sa_meetings_page_with_supergraph', false)

  const supergraphResult = useGetMeetingByEitherIdSupergraphQuery(
    { meetingId },
    { skip: skip || !shouldUseSupergraph || !meetingId, ...queryOptions },
  )

  const supergraphSettled = !supergraphResult.isLoading && !supergraphResult.isUninitialized
  const shouldFallbackToSearch = !shouldUseSupergraph
    || (supergraphSettled && isSupergraphResultEmpty(supergraphResult.data))

  const searchResult = useGQLGetMeetingQuery(
    {
      uuid: meetingId,
      recapScope: MEETINGS_RECAP_SCOPE.ALL_RECAPS,
    },
    {
      skip: skip || !meetingId || (shouldUseSupergraph && !shouldFallbackToSearch),
      ...queryOptions,
    },
  )

  if (!shouldUseSupergraph) {
    return searchResult
  }

  if (!isSupergraphResultEmpty(supergraphResult.data) || !supergraphSettled) {
    return supergraphResult
  }

  return searchResult
}
